import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { API, DayTimeHoursDTO, DayTimeIntervalDTO, WeeklyScheduleDTO } from '../../../../services/api/api';
import { UtilService } from '../../../../services/util.service';
import { ErrorService } from '../../../../services/error.service';
import { ActivatedRoute } from '@angular/router';
import { WeeklyScheduleService } from '../../../../services/date-time/weekly-schedule.service';
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DailyHourRangeValidatorService } from '../../../../services/date-time/daily-hour-range-validator.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-comp-weekly-cycle-list-edit',
  templateUrl: './weekly-schedule-edit.component.html',
  styleUrls: ['./weekly-schedule-edit.component.scss'],
  standalone: false,
})
export class WeeklyScheduleEditComponent implements OnInit {
  weeklySchedule: WeeklyScheduleDTO;
  weeklyScheduleId: number;
  loading: boolean;
  cycleValid: boolean;

  daysValid: { [key: string]: boolean };
  form = new UntypedFormGroup({
    Name: new FormControl('', [Validators.required]),
    Description: new FormControl('', []),
  });
  suggestion: string;

  constructor(
    private api: API,
    public util: UtilService,
    public cdRef: ChangeDetectorRef,
    private errorHandler: ErrorService,
    private activatedRoute: ActivatedRoute,
    public weeklyScheduleService: WeeklyScheduleService,
    private rangeValidator: DailyHourRangeValidatorService,
    private destroyRef: DestroyRef
  ) {
    this.suggestion = '-';
    this.loading = true;
    this.weeklySchedule = null;
    this.weeklyScheduleId = null;
    this.daysValid = {};
    this.weeklyScheduleService.dayKeys.forEach(day => (this.daysValid[day] = true));
    this.cycleValid = false;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.weeklyScheduleId = +params.weeklyScheduleID;
      if (this.weeklyScheduleId) {
        this.refreshFromServer();
      } else {
        this.weeklySchedule = new WeeklyScheduleDTO();
        this.createForm();
      }
    });
    this.registerFormChangeListener();
  }

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  createForm() {
    this.loading = true;
    this.suggestion = this.weeklyScheduleService.toString(this.weeklySchedule);

    if (this.weeklySchedule?.name) {
      this.Name.setValue(this.weeklySchedule?.name);
      this.Name.markAsTouched();
    }
    if (this.weeklySchedule?.description) {
      this.Description.setValue(this.weeklySchedule?.description);
      this.Description.markAsTouched();
    }

    // add controls to form
    Object.keys(this.weeklySchedule).forEach(dayKey => {
      const dayTimeHours: DayTimeHoursDTO = this.weeklySchedule[dayKey];
      if (dayTimeHours?.intervals?.length > 0) {
        let c = 0;
        dayTimeHours.intervals.forEach(() => {
          this.addFormControl(dayKey, c);
          c++;
        });
      }
    });

    this.validateCycle();
    this.loading = false;
  }

  registerFormChangeListener() {
    this.Name.valueChanges.subscribe(name => {
      this.weeklySchedule.name = name;
      this.validateCycle();
    });
    this.Description.valueChanges.subscribe(name => {
      this.weeklySchedule.description = name;
    });
  }

  addFormControl(dayKey: string, index: number) {
    const dayTimeHours: DayTimeHoursDTO = this.weeklySchedule[dayKey];
    const r: DayTimeIntervalDTO = dayTimeHours.intervals[index];

    const fromControl = new UntypedFormControl(('00' + r.fromHour).slice(-2) + ':' + ('00' + r.fromMinute).slice(-2), [
      Validators.required,
      this.timeInputValidator(dayKey, index, 'from'),
    ]);
    const tillControl = new UntypedFormControl(('00' + r.tillHour).slice(-2) + ':' + ('00' + r.tillMinute).slice(-2), [
      Validators.required,
      this.timeInputValidator(dayKey, index, 'till'),
    ]);

    this.form.addControl(dayKey + '_' + index + '_from', fromControl);
    this.form.addControl(dayKey + '_' + index + '_till', tillControl);

    this.cdRef.detectChanges();
  }

  removeFormControl(dayKey: string, index: number) {
    this.form.removeControl(dayKey + '_' + index + '_from');
    this.form.removeControl(dayKey + '_' + index + '_till');
  }

  addTimeRange(dayKey: string): void {
    let dayTimeHours: DayTimeHoursDTO = this.weeklySchedule[dayKey];
    if (!dayTimeHours) {
      this.weeklySchedule[dayKey] = new DayTimeHoursDTO();
      dayTimeHours = new DayTimeHoursDTO();
      this.daysValid[dayKey] = true;
    }

    if (!dayTimeHours.intervals) {
      this.weeklySchedule[dayKey].intervals = [];
      dayTimeHours.intervals = [];
    }

    const timeInterval = new DayTimeIntervalDTO();
    timeInterval.fromHour = 0;
    timeInterval.fromMinute = 0;
    timeInterval.tillHour = 23;
    timeInterval.tillMinute = 59;

    this.weeklySchedule[dayKey].intervals.push(timeInterval);

    const rangeIndex = this.weeklySchedule[dayKey].intervals.length - 1;
    this.addFormControl(dayKey, rangeIndex);
    this.validateTimeRangeAndUpdateSuggestion(dayKey);
    this.cdRef.detectChanges();
  }

  removeTimeRange(dayKey: string, rangeIndex: number): void {
    if (this.weeklySchedule?.[dayKey]?.intervals) {
      if (this.weeklySchedule[dayKey].intervals.length > 1) {
        this.removeFormControl(dayKey, rangeIndex);
        this.weeklySchedule[dayKey].intervals.splice(rangeIndex, 1);
      } else {
        this.weeklySchedule[dayKey] = null;
        this.daysValid[dayKey] = true;
      }
      this.validateTimeRangeAndUpdateSuggestion(dayKey);
    }
  }

  onChange(dayKey: string, rangeIndex: number, rangeType: string, $event: any) {
    const [hour, minute] = this.rangeValidator.getHourAndMinuteFromTimeString($event?.target?.value);

    this.weeklySchedule[dayKey].intervals[rangeIndex][rangeType + 'Hour'] = hour;
    this.weeklySchedule[dayKey].intervals[rangeIndex][rangeType + 'Minute'] = minute;

    $event.target.value = this.getTimeString(dayKey, rangeIndex, rangeType);
    this.hasError(dayKey, rangeIndex, rangeType);
    this.validateTimeRangeAndUpdateSuggestion(dayKey);
  }

  validateTimeRangeAndUpdateSuggestion(dayKey: string) {
    this.hasValidRanges(dayKey);
    if (this.validateDays()) {
      this.suggestion = this.weeklyScheduleService.toString(this.weeklySchedule);
    }
    this.validateCycle();
  }

  hasError(dayKey: string, rangeIndex: number, rangeType: string): boolean {
    const input = this.form.get(dayKey + '_' + rangeIndex + '_' + rangeType);

    if (input) {
      return !!(input.touched && input.errors);
    }
    return false;
  }

  getTimeString(dayKey: string, rangeIndex: number, rangeType: string): string {
    const hourRange = this.weeklySchedule[dayKey].intervals[rangeIndex];
    return ('00' + hourRange[rangeType + 'Hour']).slice(-2) + ':' + ('00' + hourRange[rangeType + 'Minute']).slice(-2);
  }

  timeInputValidator(dayKey: string, index: number, type: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const time = control.value;
      let [hour, minute] = time.split(':');
      hour = +hour;
      minute = +minute;

      // copy of range
      if (this.weeklySchedule?.[dayKey]?.intervals?.[index]) {
        const r = JSON.parse(JSON.stringify(this.weeklySchedule[dayKey].intervals[index]));
        r[type + 'Hour'] = hour;
        r[type + 'Minute'] = minute;
        if (r.fromHour > r.tillHour || (r.fromHour === r.tillHour && r.fromMinute >= r.tillMinute)) {
          return { invalidTime: true };
        }
      }

      if (isNaN(hour) || isNaN(minute)) {
        return { invalidTime: true };
      }

      return null;
    };
  }

  hasValidRanges(dayKey: string): boolean {
    if (this.weeklySchedule?.[dayKey]) {
      const dayRange: DayTimeIntervalDTO[] = this.weeklySchedule[dayKey].intervals;
      const result = this.rangeValidator.hasValidIntervals(dayRange);
      this.daysValid[dayKey] = result;
      return result;
    }
    return true;
  }

  validateCycle() {
    const validName: boolean = !!(
      this.weeklySchedule.name &&
      this.weeklySchedule.name.length > 0 &&
      this.weeklySchedule.name.trim().length > 0
    );
    this.cycleValid = Object.values(this.daysValid).indexOf(false) === -1 && validName;
  }

  validateDays(): boolean {
    return Object.values(this.daysValid).indexOf(false) === -1;
  }

  submit(): void {
    if (this.weeklySchedule && this.cycleValid) {
      this.loading = true;
      (this.weeklyScheduleId
        ? this.api.ca_md_WeeklySchedule_editWeeklySchedule(this.weeklySchedule)
        : this.api.ca_md_WeeklySchedule_createWeeklySchedule(this.weeklySchedule)
      )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.util.returnToNavigation(this.util.RoutingID.weeklySchedule);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  private refreshFromServer() {
    if (this.weeklyScheduleId) {
      this.api
        .ca_md_WeeklySchedule_retrieveById(this.weeklyScheduleId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (weeklySchedule: WeeklyScheduleDTO) => {
            this.weeklySchedule = weeklySchedule;
            this.createForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  applySuggestion() {
    this.Description.setValue(this.suggestion);
  }
}
